/**
 * AI CONTEXT
 * Purpose: evaluation form action context (approve/reject/print + shared maps).
 * Inputs: students/users/traits + current user + semester filter.
 * Writes: evaluation status transitions and eval event logs.
 * Used by: EvaluationFormModal/EvaluationForm stack.
 * Refactor: isolate status transition side effects into a service module.
 */
import { App } from "antd"
import { deleteField } from "firebase/firestore"
import { keyBy } from "lodash"
import { createContext, useCallback, useContext, useMemo } from "react"

import { evalTraitAtoms } from "#atoms"
import Firebase from "#classes/Firebase"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import { EVAL_EVENT_TYPE } from "#customTypes/composedTypes/evalCommentTypes"
import {
  EVALUATION_STATUS,
  EvaluationConstructor,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useGetAllStudents } from "#hooks/jotai/useGetStudents"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"
import usePrintEval from "#hooks/newHooks/usePrintEval"
import { markEvaluationPrinted } from "#utils/evalCommentHelpers"
import { getClampedEvalSemester } from "#utils/evaluationHelpers"
import { ErrorType, formatError } from "#utils/textFormatters"

interface EvaluationFormContextType {
  usersById: Record<Uuid, UserConstructor>
  studentsById: Record<Uuid, StudentConstructor>
  traitsById: Record<Uuid, EvaluationTrait>
  onApprove: (evaluationId: Uuid) => void
  onReject: (evaluationId: Uuid) => void
  onPrint: (
    evaluationId: Uuid,
    evaluation: EvaluationConstructor,
    traitsById: Record<Uuid, EvaluationTrait>
  ) => void
}

const EvaluationFormContext = createContext<EvaluationFormContextType | null>(
  null
)

export const EvaluationFormContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { currentUserId, getUserSetting } = useUserContext()
  const { message } = App.useApp()

  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.EVALUATIONS_TABLE_SEMESTER
  )
  const clampedSemester = getClampedEvalSemester(filterSemester)

  const evalTraitsQuery = useMemo(
    () => ({
      queries: {
        semesters: {
          value: clampedSemester,
          comparator: COMPARATORS.CONTAINS,
        },
      },
    }),
    [clampedSemester]
  )

  const evalTraits = useFilteredDocs<EvaluationTrait>(
    evalTraitAtoms,
    DOC_TYPES.EVAL_TRAITS,
    evalTraitsQuery
  )

  const students = useGetAllStudents()
  const users = useGetAllUsers()

  const studentsById = useMemo(() => keyBy(students, "id"), [students])
  const usersById = useMemo(() => keyBy(users, "id"), [users])
  const traitsById = useMemo(() => keyBy(evalTraits, "id"), [evalTraits])

  const handleApprove = useCallback(
    async (evaluationId: Uuid) => {
      try {
        await Firebase.getInstance().updateEvaluationWithEvent({
          evaluationId,
          patch: {
            status: EVALUATION_STATUS.APPROVED,
            approvedBy: currentUserId,
            approvedAt: new Date().toISOString(),
            rejectedBy: deleteField(),
            rejectedAt: deleteField(),
          },
          event: currentUserId
            ? { authorId: currentUserId, eventType: EVAL_EVENT_TYPE.APPROVED }
            : undefined,
        })
        void message.success("Evaluation approved successfully.")
      } catch (error) {
        void message.error(
          `Failed to approve evaluation: ${formatError(error as ErrorType)}`
        )
      }
    },
    [currentUserId, message]
  )

  const handleReject = useCallback(
    async (evaluationId: Uuid) => {
      try {
        await Firebase.getInstance().updateEvaluationWithEvent({
          evaluationId,
          patch: {
            status: EVALUATION_STATUS.REJECTED,
            rejectedBy: currentUserId,
            rejectedAt: new Date().toISOString(),
            approvedBy: deleteField(),
            approvedAt: deleteField(),
          },
          event: currentUserId
            ? { authorId: currentUserId, eventType: EVAL_EVENT_TYPE.REJECTED }
            : undefined,
        })
        void message.success("Evaluation rejected successfully.")
      } catch (error) {
        void message.error(
          `Failed to reject evaluation: ${formatError(error as ErrorType)}`
        )
      }
    },
    [currentUserId, message]
  )

  const { printEval } = usePrintEval()

  const handlePrint = useCallback(
    async (
      evaluationId: Uuid,
      evaluation: EvaluationConstructor,
      traitsById: Record<Uuid, EvaluationTrait>
    ) => {
      try {
        await printEval({
          studentName: studentsById[evaluation.studentId].name,
          evaluation,
          traitsById,
        })
      } catch (error) {
        void message.error(
          `Failed to print evaluation: ${formatError(error as ErrorType)}`
        )
        return
      }

      try {
        await markEvaluationPrinted(evaluationId, currentUserId)
        void message.success("Evaluation printed successfully.")
      } catch (error) {
        void message.error(
          `Evaluation printed, but saving its printed status failed: ${formatError(error as ErrorType)}. Print again to retry.`
        )
      }
    },
    [currentUserId, message, printEval, studentsById]
  )

  return (
    <EvaluationFormContext.Provider
      value={{
        usersById,
        studentsById,
        traitsById,
        onApprove: handleApprove,
        onReject: handleReject,
        onPrint: handlePrint,
      }}
    >
      {children}
    </EvaluationFormContext.Provider>
  )
}

const useEvaluationFormContext = <T,>(
  callback: (state: EvaluationFormContextType) => T
) => {
  const context = useContext(EvaluationFormContext)
  if (!context) {
    throw new Error(
      "useEvaluationFormContext must be used within an EvaluationFormContextProvider"
    )
  }
  return callback(context)
}

export default useEvaluationFormContext
